import { z } from 'zod'

import type { CalendarDay } from '@/shared/ui'

import { rolesPath } from '../roles'

/** A User Profile's roles: GET lists them, POST assigns one. */
export function roleAssignmentsPath(userId: string): string {
  return `/access-control/v1/user-profiles/${encodeURIComponent(userId)}/role-assignments`
}

/** One of a User Profile's roles: PUT changes its dates, DELETE revokes it. */
export function roleAssignmentPath(userId: string, assignmentId: string): string {
  return `${roleAssignmentsPath(userId)}/${encodeURIComponent(assignmentId)}`
}

/** One role's path, for its details. */
export function rolePath(roleId: string): string {
  return `${rolesPath}/${encodeURIComponent(roleId)}`
}

/** The query parameter that presets a role on Create User, such as Classes' "Create Instructor" link. */
export const presetRoleParameter = 'role'

/** The API's code for an assignment the profile no longer holds, as after a revoke whose answer was lost. */
export const roleAssignmentNotFound = 'access_control.role_assignment_not_found'

/** The API's code for a role that is gone, or that the caller may not assign. */
export const roleNotFound = 'access_control.role_not_found'

/** A role a User Profile holds. */
export interface RoleAssignment {
  /** Such as `rla_2y1VdX9p7k3FZ8mH6nQaRs`. */
  readonly id: string
  readonly roleId: string
  readonly roleName: string
  readonly isRoleEnabled: boolean
  /** ISO 8601, or `null` for at once. */
  readonly startsOn: string | null
  /** ISO 8601, or `null` for never. */
  readonly expiresOn: string | null
  /** Whether the caller may change its dates or revoke it. */
  readonly mayChange: boolean
}

const assignmentSchema = z.object({
  id: z.string(),
  roleId: z.string(),
  roleName: z.string(),
  isRoleEnabled: z.boolean(),
  startsOn: z.string().nullish(),
  expiresOn: z.string().nullish(),
  mayChange: z.boolean(),
})

/** Narrows one assignment; throws on anything else. */
export function parseRoleAssignment(body: unknown): RoleAssignment {
  const parsed = assignmentSchema.parse(body)
  return {
    id: parsed.id,
    roleId: parsed.roleId,
    roleName: parsed.roleName,
    isRoleEnabled: parsed.isRoleEnabled,
    startsOn: parsed.startsOn ?? null,
    expiresOn: parsed.expiresOn ?? null,
    mayChange: parsed.mayChange,
  }
}

/** A User Profile's roles, and whether the viewer may assign it a role. */
export interface ProfileRoles {
  readonly assignments: readonly RoleAssignment[]
  /** The list's `assign` link: the viewer holds `role-assignments` write over the profile and has a role to give. */
  readonly mayAssign: boolean
}

/** Narrows the list of a User Profile's roles, which comes as one page. */
export function parseRoleAssignments(body: unknown): ProfileRoles {
  const parsed = z
    .object({ data: z.array(z.unknown()), _links: z.record(z.string(), z.unknown()).optional() })
    .transform(({ data, _links: links }) => ({ data, links }))
    .parse(body)
  return { assignments: parsed.data.map(parseRoleAssignment), mayAssign: parsed.links?.['assign'] !== undefined }
}

/** What an assignment's dates make it, besides current. */
export type AssignmentState = 'expired' | 'notYetActive' | 'disabled'

/** What an assignment's state is worked out from. */
export type AssignmentTerms = Pick<RoleAssignment, 'startsOn' | 'expiresOn' | 'isRoleEnabled'>

/** Why an assignment passes no check now: its dates, or a disabled role. */
export function assignmentStates(assignment: AssignmentTerms, now: Date): readonly AssignmentState[] {
  const states: AssignmentState[] = []
  if (assignment.expiresOn !== null && new Date(assignment.expiresOn) <= now) {
    states.push('expired')
  } else if (assignment.startsOn !== null && new Date(assignment.startsOn) > now) {
    states.push('notYetActive')
  }
  if (!assignment.isRoleEnabled) {
    states.push('disabled')
  }
  return states
}

/**
 * The calendar day of an assignment date. The screens pick whole days, sent as midnight UTC, so
 * the day is read back in UTC and shows the same in every time zone.
 */
export function dayOfDate(value: string | null): CalendarDay | '' {
  return value === null ? '' : new Date(value).toISOString().slice(0, 10)
}

/** The date the API takes for a picked day, or `null` for none. */
export function dateOfDay(day: CalendarDay | ''): string | null {
  return day === '' ? null : `${day}T00:00:00Z`
}
